import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";

import { OuraLogo } from "@/components/icons";
import {
  OuraError,
  OuraLoader,
  OuraNoData,
  OuraSyncButton,
  OuraSyncModal,
  OuraSyncStatus,
} from "@/domains/health/oura/components";
import { useOuraSmartSync } from "@/domains/health/oura/hooks/useOuraSmartSync";
import { useOuraSync } from "@/domains/health/oura/hooks/useOuraSync";
import { useOuraTokenStatus } from "@/domains/health/oura/hooks/useOuraTokenStatus";
import { useGlobalProgress } from "@/shared/contexts";

import { useOuraData } from "./contexts/OuraDataContext";

/**
 * WHAT:
 *   Extrahiert eine Fehlermeldung aus einem unbekannten Fehlerobjekt.
 *
 * WHY:
 *   Normalisiert verschiedene Fehlertypen zu einem String für die Anzeige.
 *
 * @param error_ - Fehlerobjekt
 *
 * @returns Fehlermeldung oder null
 *
 */
const getErrorMessage = (error_: unknown): null | string => {
  if (error_ instanceof Error) {
    return error_.message;
  }
  if (error_ === null || error_ === undefined) {
    return null;
  }
  if (typeof error_ === "string") {
    return error_;
  }
  if (typeof error_ === "object" && "message" in error_) {
    const messageValue = (error_ as { message: unknown }).message;
    if (typeof messageValue === "string") {
      return messageValue;
    }
    if (typeof messageValue === "number" || typeof messageValue === "boolean") {
      return String(messageValue);
    }
    return "Unknown error";
  }
  if (typeof error_ === "number" || typeof error_ === "boolean") {
    return String(error_);
  }
  return "Unknown error";
};

/**
 * **OuraAppContent**
 *
 * Innerer Inhalt der Oura-App mit Tab-Navigation.
 * Verwendet den zentralen Data Provider für optimierte Performance.
 *
 * @returns {JSX.Element} Oura app content component
 *
 */
const OuraAppContent = () => {
  const ouraDataResult = useOuraData();
  const ouraData = ouraDataResult.data;
  const loading = ouraDataResult.loading;
  const error = ouraDataResult.error;
  const reload = ouraDataResult.reload;

  const tokenStatus = (useOuraTokenStatus as () => "fehlt" | "geladen")();

  type OuraSyncHandler = (onNoData: () => void) => Promise<void>;
  const handleSync = (useOuraSync as (reload: () => unknown) => OuraSyncHandler)(reload);
  const progressContext = useGlobalProgress();
  const complete = progressContext.complete;
  const [showSyncModal, setShowSyncModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const errorMessage = error ? getErrorMessage(error) : null;

  /**
   * WHAT:
   *   Führt einen manuellen Sync aus und signalisiert Abschluss an den globalen Progress‑Indicator.
   *
   * WHY:
   *   Kombiniert Sync-Logik mit Progress-Management.
   *
   * @returns {Promise<void>}
   *
   */
  const handleSyncWithProgress = async (): Promise<void> => {
    if (typeof handleSync === "function") {
      await handleSync(() => {
        setShowSyncModal(true);
      });
    }
    if (typeof complete === "function") {
      complete();
    }
  };

  const {
    error: smartSyncError,
    result: smartSyncResult,
    running: smartSyncRunning,
  } = useOuraSmartSync();
  const smartSyncErrorMessage = getErrorMessage(smartSyncError);

  // Ref um zu verhindern, dass der initiale Sync mehrfach läuft
  const initialSyncDone = useRef(false);
  const intervalReference = useRef<NodeJS.Timeout | null>(null);
  const [lastSyncTime] = useState<Date | null>(null);

  // Lade Daten beim ersten Mount der Oura-Seite
  useEffect(() => {
    if (!loading && !ouraData && !initialSyncDone.current) {
      initialSyncDone.current = true;
      if (typeof reload === "function") {
        reload().catch(() => {
          // Error handling is done in the hook
        });
      }
    }

    // Reason: Effect should only run when loading or ouraData changes, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload is stable from context
  }, [loading, ouraData]);

  // Cleanup beim Unmount
  useEffect(() => {
    return () => {
      if (intervalReference.current) {
        clearInterval(intervalReference.current);
        intervalReference.current = null;
      }
    };
  }, []);

  // Tab-Navigation
  const tabs = [
    {
      id: "dashboard",
      label: "Dashboard",
      path: "/nexus/health/oura/dashboard",
    },
    {
      id: "readiness",
      label: "Readiness",
      path: "/nexus/health/oura/readiness",
    },
    { id: "sleep", label: "Sleep", path: "/nexus/health/oura/sleep" },
    { id: "activity", label: "Activity", path: "/nexus/health/oura/activity" },
    { id: "stress", label: "Stress", path: "/nexus/health/oura/stress" },
  ];

  const currentTab = tabs.find((tab) => location.pathname === tab.path) ?? tabs[0];

  const handleTabClick = (path: string): void => {
    // Reason: navigate returns void, not a Promise in react-router-dom v6

    navigate(path);
  };

  // NOTE: Activity data is available via ouraData?.activity if needed

  return (
    <div className="min-h-screen bg-background p-6 space-y-6 text-primary">
      <header className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <OuraLogo size="app" />
          <h1 className="text-2xl font-bold">Oura Health</h1>
        </div>
      </header>

      {errorMessage ? <OuraError message={errorMessage} /> : null}
      {smartSyncError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">Smart Sync Fehler: {smartSyncErrorMessage}</p>
        </div>
      ) : null}
      {loading ? <OuraLoader /> : null}

      {!loading && ouraData ? (
        <>
          {/* Tab Navigation */}
          <nav className="border-b border-neutral-700">
            <div className="flex items-center justify-between">
              <div className="flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      currentTab?.id === tab.id
                        ? "border-blue-500 text-blue-400"
                        : "border-transparent text-neutral-400 hover:text-neutral-300 hover:border-neutral-600"
                    }`}
                    key={tab.id}
                    onClick={() => {
                      handleTabClick(tab.path);
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <OuraSyncButton />
            </div>
          </nav>

          {/* Tab Content */}
          <div className="space-y-6">
            <Outlet />
          </div>

          <OuraSyncStatus
            lastSyncTime={lastSyncTime ?? null}
            smartSyncResult={smartSyncResult ?? ""}
            smartSyncRunning={smartSyncRunning}
            syncedAt={(() => {
              if (typeof ouraData.synced_at === "string") {
                return ouraData.synced_at;
              }
              return null;
            })()}
            tokenStatus={typeof tokenStatus === "string" ? tokenStatus : "fehlt"}
          />
        </>
      ) : null}

      {!loading && !ouraData && <OuraNoData />}

      <OuraSyncModal
        onClose={() => {
          setShowSyncModal(false);
        }}
        onRetry={async () => {
          setShowSyncModal(false);
          await handleSyncWithProgress();
        }}
        open={showSyncModal}
      />
    </div>
  );
};

/**
 * **OuraApp**
 *
 * Erweiterte Oura-App mit Tab-Navigation für detaillierte Analyse.
 * Verwendet den globalen Data Provider für optimierte Performance.
 *
 * Features:
 * 1. Tab-Navigation für verschiedene Gesundheitsmetriken
 * 2. Dashboard-Übersicht als Haupttab
 * 3. Detaillierte Analyse-Tabs für Readiness, Sleep, Activity, Heartrate
 * 4. Alle bestehenden Sync-Features
 * 5. Optimierte Performance durch zentralen Data Provider
 *
 * @returns {JSX.Element} Oura app component
 *
 */
export const OuraApp = () => {
  return <OuraAppContent />;
};
